import React from "react";
import { DocumentPickerAsset } from "expo-document-picker";
import { ImagePickerAsset } from "expo-image-picker";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "lodash";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";

import { useAccountActions } from "~/components/AccountActions/context";
import SaveFormStack from "~/components/SaveForm/SaveForm.stack";
import {
  baseSchema,
  fileSchema,
  mediaSchema,
  noteSchema,
  SaveFormValues,
  urlSchema,
  useSaveFormContext,
} from "~/components/SaveForm/shared";
import { useHonoClient } from "~/hooks/HonoContext";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { isLocalhost, isWeb } from "~/lib/constants";
import { generateObjectId } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";

// TODO: Make the "steps" a state machine, current implementation is so shit
export default function SaveFormFormProvider() {
  const { store } = useAccountStore();

  // TODO: For some reason params are nested when using `useLocalSearchParams` :shrug:
  // we freeze params cuz after push/replace within this stack, params can be lost

  const { getTempState } = useAccountActions();

  const defaultValues = React.useMemo(
    function getDefaultValues(): SaveFormValues {
      //#region update defaultValues
      {
        const tempParams = getTempState<"editSave">() ?? {};
        if (tempParams.id) {
          const space = store.spaceById(tempParams.spaceId);
          if (!space) throw new Error("Workspace not found");

          const save = space?.saveById(tempParams.id);
          if (!save) throw new Error("Save not found");

          const common = {
            intent: "update",
            id: save.id,
            description: save.description ?? "",
            title: save.title ?? "",
            space: {
              id: tempParams.spaceId,
              collections: save.collectionsAsArray().map((c) => c.id),
              tags: save.tagsAsArray().map((t) => t.id),
            },
            type: save.type,
          } as const;

          switch (save.type) {
            case "URL":
              return {
                ...common,
                step: "form",
                type: save.type,

                url: save.url,
                urlMeta: {
                  ...save.meta,
                  title: save.meta?.title ?? "",
                  description: save.meta?.description ?? "",
                  image: save.meta?.image ?? "",
                  logo: save.meta?.logo ?? "",
                },
              };
            case "MEDIA":
              return {
                ...common,
                step: "form",
                type: save.type,
                r2Key: save.r2Key,
                mediaMeta: {
                  ...save.meta,
                  assetId: save.meta?.assetId ?? "",
                  duration: save.meta?.duration ?? 0,
                  fileSize: save.meta?.fileSize ?? 0,
                  fileName: save.meta?.fileName ?? "",
                  height: save.meta?.height ?? 0,
                  width: save.meta?.width ?? 0,
                  // @ts-expect-error ...
                  type: save.meta?.type ?? "image",
                  mimeType: save.meta?.mimeType ?? "",
                },
              };
            case "FILE":
              return {
                ...common,
                step: "form",
                type: save.type,
                r2Key: save.r2Key,
                fileMeta: {
                  ...save.meta,
                  name: save.meta?.name ?? "",
                  size: save.meta?.size ?? 0,
                  mimeType: save.meta?.mimeType ?? "",
                },
              };

            case "NOTE": {
              return {
                ...common,
                step: tempParams.step === "takeNote" ? tempParams.step : "form",
                type: save.type,
                richTextAST: (() => {
                  try {
                    return JSON.parse(save.richTextAST);
                  } catch {
                    return {};
                  }
                })(),
              };
            }
          }
        }
      }
      //#endregion

      // using these ad-hoc scopes (the braces) so that TS can get correct types for tempParams without adding an additional if/else
      //#region create defaultValues
      {
        const tempParams = getTempState<"addSave">() ?? {};
        const common = {
          intent: "create",
          id: generateObjectId(),
          description: "",
          space: {
            id: tempParams.spaceId ?? "",
            // Fun fact, try to remove this .map, its not doing anything anyways :D
            collections: [].map((x) => x),
            tags: [].map((x) => x),
          },
        } as const;

        switch (tempParams.type) {
          case "MEDIA": {
            return {
              ...common,
              title: tempParams.mediaMeta.fileName,
              step: "showAsset",
              r2Key: "",
              mediaMeta:
                "mediaMeta" in tempParams
                  ? tempParams.mediaMeta
                  : {
                      type: "image",
                      uri: "",
                      assetId: "",
                      duration: 0,
                      fileName: "",
                      fileSize: 0,
                      height: 0,
                      width: 0,
                      mimeType: "",
                    },
              type: tempParams.type,
            };
          }

          case "FILE": {
            return {
              ...common,
              step: "showAsset",
              title: tempParams.fileMeta.name,
              r2Key: "",
              fileMeta:
                "fileMeta" in tempParams
                  ? tempParams.fileMeta
                  : {
                      uri: "",
                      name: "",
                      size: 0,
                      mimeType: "",
                    },
              type: tempParams.type,
            };
          }

          case "URL": {
            return {
              ...common,
              url: isLocalhost ? "https://example.com" : "",
              step: "addUrl",
              type: tempParams.type,
            };
          }

          case "NOTE": {
            return {
              ...common,
              step: "takeNote",
              type: tempParams.type,
              richTextAST: {},
            };
          }

          default:
            throw new Error("trying to create invalid Save type");
        }
      }
      //#endregion
    },
    [getTempState, store],
  );

  const form = useForm<SaveFormValues>({
    resolver: (...args) => {
      const data = args[0];
      switch (data.type) {
        case "URL":
          if (data.step === "addUrl" || data.step === "editUrl") {
            return zodResolver(
              urlSchema.pick({
                id: true,
                url: true,
                step: true,
                type: true,
                urlMeta: true,
              }),
            )(...args);
          }
          return zodResolver(urlSchema)(...args);

        case "MEDIA":
          if (data.step === "showAsset" || data.step === "selectLocation") {
            return zodResolver(
              mediaSchema.pick({
                id: true,
                mediaMeta: true,
                step: true,
                type: true,
                r2Key: true,
              }),
            )(...args);
          }
          return zodResolver(mediaSchema)(...args);

        case "FILE":
          if (data.step === "showAsset" || data.step === "selectLocation") {
            return zodResolver(
              fileSchema.pick({
                id: true,
                fileMeta: true,
                step: true,
                type: true,
                r2Key: true,
              }),
            )(...args);
          }
          return zodResolver(fileSchema)(...args);

        case "NOTE":
          return zodResolver(noteSchema)(...args);

        default:
          return zodResolver(baseSchema)(...args);
      }
    },
    defaultValues: defaultValues,
  });

  !isEmpty(form.formState.errors) &&
    console.log({ formErrors: form.formState.errors });

  return (
    <FormProvider {...form}>
      {isWeb ? <UploadMediaOrAsset /> : null}
      <SaveFormStack />
    </FormProvider>
  );
}

function UploadMediaOrAsset() {
  const { control, getValues, setValue } = useSaveFormContext();
  const { hc } = useHonoClient();
  const account = useCurrentAccount();
  const type = getValues("type");
  const spaceId = getValues("space.id");
  const [mediaUri, fileUri] = useWatch({
    control,
    name: ["mediaMeta.uri", "fileMeta.uri"],
  });

  const uri = mediaUri ?? fileUri;

  useQuery({
    queryKey: ["media_upload", type, spaceId, uri],
    queryFn: async () => {
      return hc.accounts[":accountId"].object
        .$put({
          param: {
            accountId: account.id,
          },
          json: (() => {
            switch (type) {
              case "MEDIA":
                return {
                  file: {
                    uri: uri!,
                    name: getValues("mediaMeta.fileName") ?? "",
                    type: getValues("mediaMeta.type"),
                    mimeType: getValues("mediaMeta.mimeType"),
                  },
                  saveId: getValues("id"),
                  customMetadata: {
                    saveId: getValues("id"),
                  },
                };
              case "FILE":
                return {
                  file: {
                    uri: uri!,
                    name: getValues("fileMeta.name") ?? "",
                    type: "document",
                    mimeType: getValues("fileMeta.mimeType") ?? "",
                  },
                  saveId: getValues("id"),
                  customMetadata: {
                    saveId: getValues("id"),
                  },
                };
              default:
                throw new Error("Invalid type");
            }
          })(),
        })
        .then((res) => res.json());
    },
    onSuccess: (data) => {
      setValue("r2Key", data.data.key);
    },
    onError: (error) => {
      console.error(error);
    },
    enabled: (type === "MEDIA" || type === "FILE") && isBase64(uri ?? ""),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  return <></>;
}

function _assertIsMedia(x: any): asserts x is ImagePickerAsset {
  if (isWeb) {
    if (!x.mimeType.includes("image/") && !x.mimeType.includes("video/"))
      throw new Error("x is not a media type");
  } else if (x.type !== "video" && x.type !== "image")
    throw new Error("x is not a media type");
}

function _assertIsFile(x: any): asserts x is DocumentPickerAsset {
  if (!x.name) throw new Error("x is not a file type");
}

function isBase64(str: string) {
  if (!str) return false;
  return str.startsWith("data:");
}
